import { Alert, Box, Button, Chip, Drawer, Stack, Typography } from "@mui/material";
import { useState } from "react";
import RelatedJournalEntries from "@/components/accounting/RelatedJournalEntries";
import { SOURCE_TYPE_CONSUMPTION_EVENT } from "@/constants/accounting";
import { useTesterAction } from "@/hooks/useCampaignTester";
import type { TesterAction, TesterContactRow } from "@/interfaces/campaignTester";
import { apiErrorMessage } from "@/utils/apiError-helper";

/** Every action's three effects, stated up front — including the ones that are deliberately none. */
const ACTIONS: {
  action: TesterAction;
  label: string;
  from: string[];
  contact: string;
  credits: string | null;
  ledger: string | null;
  /**
   * Which notification mode this action belongs to. A campaign is one or the other, and showing
   * both would suggest a choice that does not exist: the send commits, the link does not, and which
   * applies is fixed by how the client distributes.
   */
  mode?: "gfg" | "manual";
}[] = [
  {
    action: "sends",
    label: "Send gift email",
    from: ["ready"],
    contact: "step → sent, sent_at stamped",
    // The credits left when the contact was CREATED — prepaid + GFG-emails commits at assignment,
    // because the send may be months away on this recipient's own date. The send finds that
    // commitment and returns it.
    credits: "none — already committed when the contact was created",
    ledger: null,
    mode: "gfg",
  },
  {
    action: "links",
    label: "Generate gift link",
    from: ["ready"],
    contact: "step → sent, sent_at stamped",
    // The point of the mode: we never see the send, so there is no moment to commit at. The budget
    // still caps the campaign; the credits simply stay in the lots until someone redeems.
    credits: null,
    ledger: null,
    mode: "manual",
  },
  {
    action: "opens",
    label: "Recipient opens invite",
    from: ["sent"],
    contact: "email_opened_at stamped",
    credits: null,
    ledger: null,
  },
  {
    action: "redemptions",
    label: "Recipient selects item — redeem",
    from: ["sent"],
    contact: "step → redeemed, redeemed_at stamped",
    credits:
      "commitment resolves — or, with no commitment, draws FIFO here; consumption_events; budget consumed ↑",
    ledger: "DR deferred revenue / CR pending fulfilment",
  },
  {
    action: "orders",
    label: "Order created in Shopify",
    from: ["redeemed"],
    contact: "order_id stamped (simulated)",
    credits: null,
    ledger: null,
  },
  {
    action: "dispatches",
    label: "Order dispatched",
    from: ["redeemed", "shipped"],
    contact: "step → shipped, tracking stamped",
    credits: null,
    ledger: "DR pending fulfilment / CR credits redeemed — revenue recognised here",
  },
];

const LIFECYCLE = ["ready", "sent", "redeemed", "shipped", "delivered"];

function Effect({ label, value }: { label: string; value: string | null }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 0.4 }}>
      <Typography
        sx={{
          width: 62,
          flexShrink: 0,
          fontSize: 9.5,
          letterSpacing: "1.2px",
          textTransform: "uppercase",
          fontWeight: 700,
          color: "var(--color-graphite)",
          pt: "2px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          color: value ? "var(--color-rodeo-black)" : "var(--color-rodeo-dust)",
          fontStyle: value ? "normal" : "italic",
        }}
      >
        {value ?? "no effect"}
      </Typography>
    </Stack>
  );
}

/**
 * The contact's lifecycle and the actions legal from where it currently stands.
 *
 * Illegal actions are shown DISABLED with the reason rather than hidden — the drawer is meant to
 * teach the state machine, not just drive it.
 */
export default function ContactActionsDrawer({
  contact,
  campaignId,
  manualSend,
  simulatedDate,
  onClose,
}: {
  contact: TesterContactRow | null;
  campaignId: number;
  /** The campaign's notification mode — decides whether send or link is offered. */
  manualSend: boolean;
  simulatedDate: string;
  onClose: () => void;
}) {
  const run = useTesterAction();
  const [error, setError] = useState<string | null>(null);

  const step = contact?.step ?? "";
  const stepIndex = LIFECYCLE.indexOf(step);

  function fire(action: TesterAction) {
    if (!contact) return;
    setError(null);
    run.mutate(
      {
        action,
        campaignId,
        contactId: contact.contactId,
        // The simulated clock: an ISO instant, so an expiry window can be exercised without waiting.
        occurredAt: simulatedDate
          ? new Date(`${simulatedDate}T12:00:00Z`).toISOString()
          : undefined,
      },
      {
        onError: (e: unknown) => setError(apiErrorMessage(e, "The action was refused.")),
      },
    );
  }

  return (
    <Drawer anchor="right" open={contact != null} onClose={onClose}>
      <Box sx={{ width: 520, maxWidth: "94vw", p: 3 }}>
        {contact && (
          <>
            <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 20 }}>
              {contact.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mb: 2 }}>
              {contact.email} · contact #{contact.contactId}
            </Typography>

            {/* Lifecycle */}
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
              {LIFECYCLE.map((s, i) => (
                <Chip
                  key={s}
                  size="small"
                  label={s}
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    bgcolor:
                      i === stepIndex
                        ? "var(--color-rodeo-black)"
                        : i < stepIndex
                          ? "var(--color-good-sage-4)"
                          : "var(--color-wax)",
                    color:
                      i === stepIndex
                        ? "#fff"
                        : i < stepIndex
                          ? "var(--color-good-sage)"
                          : "var(--color-rodeo-dust)",
                  }}
                />
              ))}
              {stepIndex === -1 && step && (
                <Chip size="small" label={step} color="error" sx={{ fontSize: 10.5 }} />
              )}
            </Stack>

            {/* Live commitment and its lot split */}
            {contact.committed != null ? (
              <Box
                sx={{
                  border: "1px solid var(--color-card-border)",
                  borderRadius: 2,
                  p: 1.5,
                  mb: 2,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                    Committed {contact.sendDate} · expires {contact.commitmentExpiry?.slice(0, 10)}
                  </Typography>
                  <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 18 }}>
                    {contact.committed}
                  </Typography>
                </Stack>
                {contact.lots.map((l) => (
                  <Stack
                    key={l.lotId}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ fontSize: 11.5, color: "var(--color-graphite)", mt: 0.5 }}
                  >
                    <span>
                      lot #{l.lotId} · expires {l.lotExpiry.slice(0, 10)}
                    </span>
                    <span>{l.amount}</span>
                  </Stack>
                ))}
                {contact.lots.length > 1 && (
                  <Typography sx={{ fontSize: 11, color: "var(--color-good-night)", mt: 1 }}>
                    Two lots, one clock — both portions expire on the commitment's date, not the
                    lots'.
                  </Typography>
                )}
              </Box>
            ) : null}

            {error && (
              <Alert severity="error" sx={{ mb: 2, fontSize: 12.5 }}>
                {error}
              </Alert>
            )}

            {/* Actions */}
            {ACTIONS.map((a) => {
              if (a.mode && a.mode !== (manualSend ? "manual" : "gfg")) return null;
              const allowed = a.from.includes(step);
              return (
                <Box
                  key={a.action}
                  sx={{
                    border: "1px solid var(--color-card-border)",
                    borderRadius: 2,
                    mb: 1,
                    overflow: "hidden",
                    opacity: allowed ? 1 : 0.55,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ p: 1.3, bgcolor: allowed ? "#fff" : "var(--color-wax)" }}
                  >
                    <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 13 }}>
                      {a.label}
                    </Typography>
                    <Button
                      size="small"
                      variant={a.ledger ? "contained" : "outlined"}
                      disabled={!allowed || run.isPending}
                      onClick={() => fire(a.action)}
                    >
                      Run
                    </Button>
                  </Stack>
                  {allowed ? (
                    <Box
                      sx={{
                        p: 1.3,
                        borderTop: "1px dashed var(--color-card-border)",
                        bgcolor: "var(--color-clear-dust)",
                      }}
                    >
                      <Effect label="Contact" value={a.contact} />
                      <Effect label="Credits" value={a.credits} />
                      <Effect label="Ledger" value={a.ledger} />
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        p: 1,
                        px: 1.6,
                        fontSize: 11,
                        color: "var(--color-good-wine)",
                        bgcolor: "var(--color-good-wine-bg)",
                        borderTop: "1px solid var(--color-good-wine-4)",
                      }}
                    >
                      Requires {a.from.join(" or ")} — this contact is “{step || "unknown"}”.
                    </Typography>
                  )}
                </Box>
              );
            })}

            {/* The entries this contact's redemption raised. Same component the invoice and
                purchase pages use — journal entries hang off (source_type, source_id), and a
                redemption's source is its consumption event, so it works here unchanged. Clicking a
                row opens the full DR/CR detail. */}
            {contact.consumptionEventId != null && (
              <RelatedJournalEntries
                sourceType={SOURCE_TYPE_CONSUMPTION_EVENT}
                sourceId={contact.consumptionEventId}
              />
            )}

            <Typography sx={{ fontSize: 11, color: "var(--color-good-night)", mt: 2 }}>
              Credit actions call the real <code>POST /credits</code> services — exactly what
              Emmanuel's flow will call. The contact-state changes are simulated, standing in for
              the campaign backend.
            </Typography>
          </>
        )}
      </Box>
    </Drawer>
  );
}
